import React, { useEffect, useRef } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import { AuthProvider, useAuth } from './context/AuthContext';
import { VistaEmpleadoProvider, useVistaEmpleado } from './context/VistaEmpleadoContext';
import { GuiaProvider } from './context/GuiaContext';
import { registerServiceWorker, subscribeToPush, requestNotificationPermission } from './utils/pwa';
import { desactivarAutofillGlobal } from './utils/desactivarAutofill';
import { iniciarUsoErp, registrarPagina } from './utils/usoErp';
import MantenimientoOverlay from './components/MantenimientoOverlay';
import InicioSegunFichaje from './components/InicioSegunFichaje';
import Layout from './components/Layout';
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import DashboardEmpleado from './pages/DashboardEmpleado';
import Clientes from './pages/Clientes';
import Presupuestos from './pages/Presupuestos';
import PresupuestoDetalle from './pages/PresupuestoDetalle';
import NovedadesPendientes from './components/guia/NovedadesPendientes';
import GuiaSinLayout from './components/guia/GuiaSinLayout';
import Menus from './pages/Menus';
import Platos from './pages/Platos';
import Ingredientes from './pages/Ingredientes';
import Bebidas from './pages/Bebidas';
import Proveedores from './pages/Proveedores';
import StockLotes from './pages/StockLotes';
import Tareas from './pages/Tareas';
import MaterialAsignado from './pages/MaterialAsignado';
import Materiales from './pages/Materiales';
import Vehiculos from './pages/Vehiculos';
import Empleados from './pages/Empleados';
import FichaEmpleado from './pages/FichaEmpleado';
import GestionEquipo from './pages/GestionEquipo';
import EmpleadoLogin from './pages/EmpleadoLogin';
import CrearContrasena from './pages/CrearContrasena';
import CalendarioEventos from './pages/CalendarioEventos';
import TabletCocina from './pages/TabletCocina';
import ComprasNecesidades from './pages/ComprasNecesidades';
import Fichaje from './pages/Fichaje';
import Finanzas from './pages/Finanzas';
import ControlCobrosUnificado from './pages/ControlCobrosUnificado';
import Migracion from './pages/Migracion';
import RestaurarBackup from './pages/RestaurarBackup';
import Ajustes from './pages/Ajustes';
import UsoErp from './pages/UsoErp';
import Etiquetas from './pages/Etiquetas';
import Asistente from './pages/Asistente';
import Marketing from './pages/Marketing/Marketing';
import MarketingBlog from './pages/Marketing/MarketingBlog';
import MarketingBlogPostDetalle from './pages/Marketing/MarketingBlogPostDetalle';
import MarketingInstagram from './pages/Marketing/MarketingInstagram';
import MarketingGoogleBusiness from './pages/Marketing/MarketingGoogleBusiness';
import MarketingWeb from './pages/Marketing/MarketingWeb';
import MarketingWebLeads from './pages/Marketing/MarketingWebLeads';
import MarketingWebAnalytics from './pages/Marketing/MarketingWebAnalytics';
import MarketingWebSeo from './pages/Marketing/MarketingWebSeo';
import MarketingWebAds from './pages/Marketing/MarketingWebAds';
import MarketingWebEmail from './pages/Marketing/MarketingWebEmail';
import MarketingWebEstado from './pages/Marketing/MarketingWebEstado';
import MarketingWebAiVisibility from './pages/Marketing/MarketingWebAiVisibility';
import MarketingWebBacklinks from './pages/Marketing/MarketingWebBacklinks';
import GestionDocumental from './pages/GestionDocumental';
import SubidaFacturas from './pages/SubidaFacturas';
import Emails from './pages/Emails';
import AsistenteChat from './components/AsistenteChat';
// Web Pública
import HomePage from './pages/public/HomePage';
import { DesayunosPage, LunchPage, CoctelesPage } from './pages/public/ServicePages';
import { BlogPage, BlogPostPage } from './pages/public/BlogPages';
import NosotrosPage from './pages/public/NosotrosPage';
import ContactoPage from './pages/public/ContactoPage';
import "@/App.css";

// Estado para <Navigate to="/login">: la ruta pedida, para volver a ella tras
// entrar (enlaces del móvil y push; Login.js la valida). Sin destino si es la
// raíz o el propio login, que no aportan nada.
const estadoDesdeLogin = (location) => {
  const desde = `${location?.pathname || ''}${location?.search || ''}${location?.hash || ''}`;
  if (!desde || desde === '/' || desde.startsWith('/login')) return undefined;
  return { desde };
};

// Protected Route Component - Requiere autenticación
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading, user } = useAuth();
  const location = useLocation();

  // Esperar a que el auth se complete antes de renderizar
  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-gray-500">Cargando...</div>
      </div>
    );
  }

  // Redirigir al login si no está autenticado (con el destino, para volver tras entrar)
  if (!isAuthenticated) {
    return <Navigate to="/login" replace state={estadoDesdeLogin(location)} />;
  }

  return children;
};

// Configuración de permisos por ruta
const routePermissions = {
  '/dashboard': { roles: ['direccion'], moduleId: 'dashboard' },
  '/dashboard-empleado': { roles: ['all'], moduleId: 'dashboard-empleado' },
  '/clientes': { roles: ['direccion', 'comercial'], moduleId: 'clientes' },
  '/presupuestos': { roles: ['direccion', 'comercial', 'encargado'], moduleId: 'presupuestos' },
  '/calendario-eventos': { roles: ['all'], moduleId: 'calendario-eventos' },
  '/fichaje': { roles: ['all'], moduleId: 'fichaje' },
  '/menus': { roles: ['direccion', 'operaciones'], moduleId: 'menus' },
  '/platos': { roles: ['direccion', 'operaciones'], moduleId: 'platos' },
  '/ingredientes': { roles: ['direccion', 'operaciones'], moduleId: 'ingredientes' },
  '/proveedores': { roles: ['direccion', 'operaciones'], moduleId: 'proveedores' },
  '/compras': { roles: ['direccion', 'operaciones'], moduleId: 'compras' },
  '/stock': { roles: ['direccion', 'operaciones'], moduleId: 'stock' },
  '/materiales': { roles: ['direccion', 'operaciones'], moduleId: 'materiales' },
  '/materiales-asignados': { roles: ['direccion', 'operaciones'], moduleId: 'materiales-asignados' },
  '/vehiculos': { roles: ['direccion', 'operaciones'], moduleId: 'vehiculos' },
  '/tablet-cocina': { roles: ['all'], moduleId: 'tablet-cocina' },
  // Etiquetas de apertura y elaboración: las imprime todo el personal
  '/etiquetas': { roles: ['all'], moduleId: 'etiquetas' },
  '/empleados': { roles: ['direccion', 'rrhh'], moduleId: 'empleados' },
  // Ficha del empleado (alta y edición): se comprueba por la ruta base /empleados
  '/empleados/nuevo': { roles: ['direccion', 'rrhh'], moduleId: 'empleados' },
  '/empleados/:id': { roles: ['direccion', 'rrhh'], moduleId: 'empleados' },
  '/gestion-equipo': { roles: ['direccion', 'encargado'], moduleId: 'gestion-equipo' },
  '/finanzas': { roles: ['direccion', 'finanzas'], moduleId: 'finanzas' },
  '/control-cobros': { roles: ['direccion', 'finanzas'], moduleId: 'control-cobros' },
  '/gestion-documental': { roles: ['direccion', 'rrhh'], moduleId: 'gestion-documental' },
  '/subida-facturas': { roles: ['direccion', 'rrhh', 'operaciones', 'comercial', 'encargado'], moduleId: 'subida-facturas' },
  '/emails': { roles: ['direccion', 'comercial'], moduleId: 'emails' },
  '/asistente': { roles: ['direccion'], moduleId: 'asistente' },
  '/migracion': { roles: ['direccion'], moduleId: 'migracion' },
  '/restaurar-backup': { roles: ['direccion'], moduleId: 'restaurar-backup' },
  '/ajustes': { roles: ['direccion'], moduleId: 'ajustes' },
  '/uso-erp': { roles: ['direccion'], moduleId: 'uso-erp' },
  // Solo redirección a /dashboard-empleado (Mi Portal se retiró)
  '/portal-empleado': { roles: ['all'], moduleId: 'portal-empleado' },
  // Marketing
  '/marketing': { roles: ['direccion'], moduleId: 'marketing' },
  '/marketing/blog': { roles: ['direccion'], moduleId: 'marketing-blog' },
  '/marketing/blog/posts/:slug': { roles: ['direccion'], moduleId: 'marketing-blog' },
  '/marketing/instagram': { roles: ['direccion'], moduleId: 'marketing-instagram' },
  '/marketing/google-business': { roles: ['direccion'], moduleId: 'marketing-google-business' },
  // Marketing > Web
  '/marketing/web': { roles: ['direccion'], moduleId: 'marketing-web' },
  '/marketing/web/leads': { roles: ['direccion'], moduleId: 'marketing-web' },
  '/marketing/web/analytics': { roles: ['direccion'], moduleId: 'marketing-web' },
  '/marketing/web/seo': { roles: ['direccion'], moduleId: 'marketing-web' },
  '/marketing/web/ads': { roles: ['direccion'], moduleId: 'marketing-web' },
  '/marketing/web/email': { roles: ['direccion'], moduleId: 'marketing-web' },
  '/marketing/web/estado': { roles: ['direccion'], moduleId: 'marketing-web' },
  '/marketing/web/ai-visibility': { roles: ['direccion'], moduleId: 'marketing-web' },
  '/marketing/web/backlinks': { roles: ['direccion'], moduleId: 'marketing-web' },
};

// /portal-empleado (Mi Portal, retirado en el lote C de Mi Inicio) redirige a
// /dashboard-empleado con la pestaña equivalente: ?tab= o /portal-empleado/<pestaña>
const PESTANA_DESDE_PORTAL = {
  dashboard: 'hoy',
  inicio: 'hoy',
  perfil: 'hoy',
  hoy: 'hoy',
  horas: 'registro',
  registro: 'registro',
  vacaciones: 'vacaciones',
  nominas: 'nominas',
  docs: 'documentos',
  documentos: 'documentos',
  firmas: 'documentos',
  cursos: 'documentos',
  obligaciones: 'obligaciones',
};

const RedireccionPortalEmpleado = () => {
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const segmento = location.pathname.replace(/^\/portal-empleado\/?/, '').split('/')[0];
  const pedida = (params.get('tab') || segmento || '').toLowerCase();
  const tab = PESTANA_DESDE_PORTAL[pedida];
  const destino = tab && tab !== 'hoy' ? `/dashboard-empleado?tab=${tab}` : '/dashboard-empleado';
  return <Navigate to={destino} replace />;
};

// Función para verificar si el usuario tiene acceso a una ruta
const checkRouteAccess = (path, user) => {
  // Obtener permisos de la ruta
  const permission = routePermissions[path];
  
  // Si no hay permisos definidos, permitir acceso
  if (!permission) return true;
  
  // Si el usuario es dirección, tiene acceso a todo
  if (user?.rol === 'direccion') return true;
  
  // SIEMPRE permitir acceso a dashboard-empleado (y a /portal-empleado, que redirige a él)
  if (path === '/dashboard-empleado' || path === '/portal-empleado') return true;

  // Etiquetas: lo pueden imprimir todos (aunque no tengan el módulo en sus permisos)
  if (path === '/etiquetas') return true;
  
  // Si el empleado tiene permisos_modulos definidos, usarlos (tienen prioridad)
  if (user?.permisos_modulos && Array.isArray(user.permisos_modulos) && user.permisos_modulos.length > 0) {
    return user.permisos_modulos.includes(permission.moduleId);
  }
  
  // Si no tiene permisos específicos, usar el sistema de roles
  if (permission.roles.includes('all')) return true;
  
  // Verificar si el rol del usuario está en los roles permitidos
  if (user?.rol && permission.roles.includes(user.rol)) return true;
  
  return false;
};

// La misma regla a partir de la ruta completa (la usa la vista de simulación al salir)
const puedeVerRuta = (pathname, user) => checkRouteAccess('/' + String(pathname || '').split('/')[1], user);

// "Simular vista empleado": el empleado simulado no puede ver esta pantalla.
// En vez de redirigir en silencio se explica, para que dirección entienda qué pasa.
const SinAccesoSimulacion = ({ nombre }) => {
  const navigate = useNavigate();
  const { salir } = useVistaEmpleado();
  const volver = () => {
    if (window.history.length > 1) navigate(-1);
    else navigate('/dashboard-empleado', { replace: true });
  };
  return (
    <div className="min-h-[100dvh] flex items-center justify-center bg-gray-50 px-4" data-testid="simulacion-sin-acceso">
      <div className="w-full max-w-md bg-white border border-amber-200 rounded-2xl shadow-sm p-6 text-center">
        <p className="text-xs font-semibold uppercase tracking-wide text-amber-700">Vista de simulación</p>
        <h1 className="mt-2 text-xl font-semibold text-gray-900">{nombre || 'Este empleado'} no tiene acceso a esta pantalla</h1>
        <p className="mt-2 text-sm text-gray-600">Con sus permisos, esta pantalla no le aparece. Si debería verla, actívala en los permisos de su ficha.</p>
        <div className="mt-5 flex flex-col sm:flex-row gap-2 justify-center">
          <button
            type="button"
            onClick={volver}
            className="h-11 px-4 rounded-lg border border-gray-300 bg-white text-gray-700 font-medium hover:bg-gray-50"
            data-testid="simulacion-volver"
          >
            Volver
          </button>
          <button
            type="button"
            onClick={salir}
            className="h-11 px-4 rounded-lg bg-amber-500 text-white font-semibold hover:bg-amber-600"
            data-testid="simulacion-salir"
          >
            Salir de la vista
          </button>
        </div>
      </div>
    </div>
  );
};

// Ruta protegida con verificación de permisos
const PermissionRoute = ({ children, path }) => {
  const { isAuthenticated, loading, user } = useAuth();
  const { usuarioEfectivo, simulando, vistaEmpleado } = useVistaEmpleado();
  const location = useLocation();

  // Register SW and push on auth
  useEffect(() => {
    if (isAuthenticated && user) {
      registerServiceWorker().then(() => {
        requestNotificationPermission().then((permission) => {
          if (permission === 'granted') {
            subscribeToPush(user.empleado_id, user.id, user.rol || user.role);
          }
        });
      });
    }
  }, [isAuthenticated, user]);
  
  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-gray-500">Cargando...</div>
      </div>
    );
  }

  if (!isAuthenticated) {
    // Para volver al destino tras entrar (enlaces del móvil y push)
    return <Navigate to="/login" replace state={estadoDesdeLogin(location)} />;
  }

  // Obtener la ruta actual para verificar permisos
  const currentPath = path || location.pathname;
  const basePath = '/' + currentPath.split('/')[1]; // Obtener la ruta base (ej: /presupuestos de /presupuestos/123)
  
  // Verificar si tiene acceso (en la vista de simulación, con el empleado simulado)
  const hasAccess = checkRouteAccess(basePath, usuarioEfectivo);
  
  if (!hasAccess && simulando) {
    return <SinAccesoSimulacion nombre={vistaEmpleado?.nombre} />;
  }

  if (!hasAccess) {
    // Redirigir al calendario si no tiene permisos
    return <Navigate to="/calendario-eventos" replace />;
  }

  return children;
};

// Public Route Component (redirect based on user type)
const PublicRoute = ({ children }) => {
  const { isAuthenticated, loading, user } = useAuth();

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-gray-500">Cargando...</div>
      </div>
    );
  }

  if (isAuthenticated) {
    // Empleado directo (login desde /empleado/login) y roles que no son
    // dirección: Mi Inicio hasta fichar y después el calendario (o su ajuste)
    const isEmpleadoDirecto = user?.isEmpleadoDirectLogin || localStorage.getItem('empleado_token');
    if (!isEmpleadoDirecto && user?.rol === 'direccion') {
      return <Navigate to="/dashboard" replace />;
    }
    return <InicioSegunFichaje />;
  }
  
  return children;
};

// Ruta "/" (también la de la app instalada): dirección al Dashboard; el resto,
// según el fichaje y su ajuste (components/InicioSegunFichaje.js)
const RaizErp = () => {
  const { isAuthenticated, loading, user } = useAuth();
  const { simulando } = useVistaEmpleado();
  const location = useLocation();

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-gray-500">Cargando...</div>
      </div>
    );
  }
  // Para volver al destino tras entrar (enlaces del móvil y push): en la raíz
  // solo hay destino si lleva ?parámetros o #ancla
  if (!isAuthenticated) return <Navigate to="/login" replace state={estadoDesdeLogin(location)} />;
  const isEmpleadoDirecto = user?.isEmpleadoDirectLogin || localStorage.getItem('empleado_token');
  if (!simulando && !isEmpleadoDirecto && user?.rol === 'direccion') {
    return <Navigate to="/dashboard" replace />;
  }
  return <InicioSegunFichaje />;
};

// Uso del ERP: registra cada cambio de pantalla (utils/usoErp.js). Va dentro
// de BrowserRouter y de la vista de simulación (en simulación no registra).
const RastreadorUso = () => {
  const location = useLocation();
  const { user } = useAuth();
  const { simulando } = useVistaEmpleado();
  const estado = useRef({ user, simulando });
  estado.current = { user, simulando };

  useEffect(() => {
    iniciarUsoErp({
      getUsuario: () => estado.current.user,
      getSimulando: () => estado.current.simulando,
    });
  }, []);

  useEffect(() => {
    registrarPagina(location.pathname, location.search);
  }, [location.pathname, location.search]);

  return null;
};

function App() {
  // Quitar el autofill del navegador (direcciones guardadas, valores
  // anteriores) en todo el ERP. Excluye login y campos de contraseña.
  useEffect(() => desactivarAutofillGlobal(), []);

  return (
    <HelmetProvider>
    <AuthProvider>
      <MantenimientoOverlay />
      <BrowserRouter>
        <VistaEmpleadoProvider puedeVerRuta={puedeVerRuta}>
        <GuiaProvider>
        <RastreadorUso />
        {/* Novedades del ERP: mini guía de cada utilidad nueva, una vez por persona (components/guia/novedades.js) */}
        <NovedadesPendientes />
        <Routes>
          {/* PUBLIC WEBSITE - Web pública de Duetto Catering */}
          <Route path="/web" element={<HomePage />} />
          <Route path="/web/desayunos" element={<DesayunosPage />} />
          <Route path="/web/lunch" element={<LunchPage />} />
          <Route path="/web/cocteles" element={<CoctelesPage />} />
          <Route path="/web/blog" element={<BlogPage />} />
          <Route path="/web/blog/:slug" element={<BlogPostPage />} />
          <Route path="/web/nosotros" element={<NosotrosPage />} />
          <Route path="/web/contacto" element={<ContactoPage />} />
          
          {/* Public Routes */}
          {/* Login route */}
          <Route path="/login" element={<PublicRoute><Login /></PublicRoute>} />
          <Route path="/empleado/login" element={<EmpleadoLogin />} />
          <Route path="/crear-contrasena" element={<CrearContrasena />} />

          {/* Protected Routes */}
          <Route
            path="/dashboard"
            element={
              <PermissionRoute>
                <Layout>
                  <Dashboard />
                </Layout>
              </PermissionRoute>
            }
          />

          <Route
            path="/dashboard-empleado"
            element={
              <PermissionRoute>
                <Layout>
                  <DashboardEmpleado />
                </Layout>
              </PermissionRoute>
            }
          />

          <Route
            path="/clientes"
            element={
              <PermissionRoute>
                <Layout>
                  <Clientes />
                </Layout>
              </PermissionRoute>
            }
          />

          <Route
            path="/presupuestos"
            element={
              <PermissionRoute>
                <Layout>
                  <Presupuestos />
                </Layout>
              </PermissionRoute>
            }
          />

          <Route
            path="/presupuestos/:id"
            element={
              <PermissionRoute>
                <PresupuestoDetalle />
                {/* Sin Layout: motor de la guía solo para las novedades */}
                <GuiaSinLayout />
              </PermissionRoute>
            }
          />

          <Route
            path="/menus"
            element={
              <PermissionRoute>
                <Layout>
                  <Menus />
                </Layout>
              </PermissionRoute>
            }
          />

          <Route
            path="/platos"
            element={
              <PermissionRoute>
                <Layout>
                  <Platos />
                </Layout>
              </PermissionRoute>
            }
          />

          <Route
            path="/ingredientes"
            element={
              <PermissionRoute>
                <Layout>
                  <Ingredientes />
                </Layout>
              </PermissionRoute>
            }
          />

          <Route
            path="/bebidas"
            element={
              <PermissionRoute>
                <Layout>
                  <Bebidas />
                </Layout>
              </PermissionRoute>
            }
          />

          <Route
            path="/proveedores"
            element={
              <PermissionRoute>
                <Layout>
                  <Proveedores />
                </Layout>
              </PermissionRoute>
            }
          />


          <Route
            path="/compras/necesidades"
            element={
              <PermissionRoute>
                <Layout>
                  <ComprasNecesidades />
                </Layout>
              </PermissionRoute>
            }
          />


          <Route
            path="/stock"
            element={
              <PermissionRoute>
                <Layout>
                  <StockLotes />
                </Layout>
              </PermissionRoute>
            }
          />

          <Route
            path="/stock-lotes"
            element={
              <PermissionRoute>
                <Layout>
                  <StockLotes />
                </Layout>
              </PermissionRoute>
            }
          />

          <Route
            path="/tareas"
            element={
              <PermissionRoute>
                <Layout>
                  <Tareas />
                </Layout>
              </PermissionRoute>
            }
          />

          <Route
            path="/materiales-asignados"
            element={
              <PermissionRoute>
                <Layout>
                  <MaterialAsignado />
                </Layout>
              </PermissionRoute>
            }
          />

          <Route
            path="/materiales"
            element={
              <PermissionRoute>
                <Layout>
                  <Materiales />
                </Layout>
              </PermissionRoute>
            }
          />

          <Route
            path="/vehiculos"
            element={
              <PermissionRoute>
                <Layout>
                  <Vehiculos />
                </Layout>
              </PermissionRoute>
            }
          />

          <Route
            path="/empleados"
            element={
              <PermissionRoute>
                <Layout>
                  <Empleados />
                </Layout>
              </PermissionRoute>
            }
          />

          <Route
            path="/empleados/nuevo"
            element={
              <PermissionRoute>
                <Layout>
                  <FichaEmpleado />
                </Layout>
              </PermissionRoute>
            }
          />

          <Route
            path="/empleados/:id"
            element={
              <PermissionRoute>
                <Layout>
                  <FichaEmpleado />
                </Layout>
              </PermissionRoute>
            }
          />

          <Route
            path="/gestion-equipo"
            element={
              <PermissionRoute>
                <Layout>
                  <GestionEquipo />
                </Layout>
              </PermissionRoute>
            }
          />

          {/* Mi Portal ya no existe: todo está en Mi Inicio (enlaces antiguos y avisos) */}
          <Route path="/portal-empleado" element={<RedireccionPortalEmpleado />} />
          <Route path="/portal-empleado/*" element={<RedireccionPortalEmpleado />} />

          <Route
            path="/calendario-eventos"
            element={
              <PermissionRoute>
                <Layout>
                  <CalendarioEventos />
                </Layout>
              </PermissionRoute>
            }
          />

          <Route
            path="/tablet-cocina"
            element={
              <PermissionRoute>
                <TabletCocina />
              </PermissionRoute>
            }
          />

          <Route
            path="/etiquetas"
            element={
              <PermissionRoute>
                <Layout>
                  <Etiquetas />
                </Layout>
              </PermissionRoute>
            }
          />

          <Route
            path="/fichaje"
            element={
              <PermissionRoute>
                <Layout>
                  <Fichaje />
                </Layout>
              </PermissionRoute>
            }
          />

          <Route
            path="/finanzas"
            element={
              <PermissionRoute>
                <Layout>
                  <Finanzas />
                </Layout>
              </PermissionRoute>
            }
          />

          <Route
            path="/control-cobros"
            element={
              <PermissionRoute>
                <Layout>
                  <ControlCobrosUnificado />
                </Layout>
              </PermissionRoute>
            }
          />

          <Route
            path="/gestion-documental"
            element={
              <PermissionRoute>
                <Layout>
                  <GestionDocumental />
                </Layout>
              </PermissionRoute>
            }
          />

          <Route
            path="/subida-facturas"
            element={
              <PermissionRoute>
                <Layout>
                  <SubidaFacturas />
                </Layout>
              </PermissionRoute>
            }
          />

          <Route
            path="/emails"
            element={
              <PermissionRoute>
                <Layout>
                  <Emails />
                </Layout>
              </PermissionRoute>
            }
          />

          <Route
            path="/migracion"
            element={
              <PermissionRoute>
                <Layout>
                  <Migracion />
                </Layout>
              </PermissionRoute>
            }
          />

          <Route
            path="/restaurar-backup"
            element={
              <PermissionRoute>
                <Layout>
                  <RestaurarBackup />
                </Layout>
              </PermissionRoute>
            }
          />

          <Route
            path="/asistente"
            element={
              <PermissionRoute>
                <Layout>
                  <Asistente />
                </Layout>
              </PermissionRoute>
            }
          />

          <Route
            path="/ajustes"
            element={
              <PermissionRoute>
                <Layout>
                  <Ajustes />
                </Layout>
              </PermissionRoute>
            }
          />
          <Route
            path="/uso-erp"
            element={
              <PermissionRoute>
                <Layout>
                  <UsoErp />
                </Layout>
              </PermissionRoute>
            }
          />

          {/* Marketing */}
          <Route
            path="/marketing"
            element={
              <PermissionRoute>
                <Layout>
                  <Marketing />
                </Layout>
              </PermissionRoute>
            }
          />
          <Route
            path="/marketing/blog"
            element={
              <PermissionRoute>
                <Layout>
                  <MarketingBlog />
                </Layout>
              </PermissionRoute>
            }
          />
          <Route
            path="/marketing/blog/posts/:slug"
            element={
              <PermissionRoute>
                <Layout>
                  <MarketingBlogPostDetalle />
                </Layout>
              </PermissionRoute>
            }
          />
          <Route
            path="/marketing/instagram"
            element={
              <PermissionRoute>
                <Layout>
                  <MarketingInstagram />
                </Layout>
              </PermissionRoute>
            }
          />
          <Route
            path="/marketing/google-business"
            element={
              <PermissionRoute>
                <Layout>
                  <MarketingGoogleBusiness />
                </Layout>
              </PermissionRoute>
            }
          />
          <Route
            path="/marketing/web"
            element={
              <PermissionRoute>
                <Layout>
                  <MarketingWeb />
                </Layout>
              </PermissionRoute>
            }
          />
          <Route
            path="/marketing/web/leads"
            element={
              <PermissionRoute>
                <Layout>
                  <MarketingWebLeads />
                </Layout>
              </PermissionRoute>
            }
          />
          <Route
            path="/marketing/web/analytics"
            element={
              <PermissionRoute>
                <Layout>
                  <MarketingWebAnalytics />
                </Layout>
              </PermissionRoute>
            }
          />
          <Route
            path="/marketing/web/seo"
            element={
              <PermissionRoute>
                <Layout>
                  <MarketingWebSeo />
                </Layout>
              </PermissionRoute>
            }
          />
          <Route
            path="/marketing/web/ads"
            element={
              <PermissionRoute>
                <Layout>
                  <MarketingWebAds />
                </Layout>
              </PermissionRoute>
            }
          />
          <Route
            path="/marketing/web/email"
            element={
              <PermissionRoute>
                <Layout>
                  <MarketingWebEmail />
                </Layout>
              </PermissionRoute>
            }
          />
          <Route
            path="/marketing/web/estado"
            element={
              <PermissionRoute>
                <Layout>
                  <MarketingWebEstado />
                </Layout>
              </PermissionRoute>
            }
          />
          <Route
            path="/marketing/web/ai-visibility"
            element={
              <PermissionRoute>
                <Layout>
                  <MarketingWebAiVisibility />
                </Layout>
              </PermissionRoute>
            }
          />
          <Route
            path="/marketing/web/backlinks"
            element={
              <PermissionRoute>
                <Layout>
                  <MarketingWebBacklinks />
                </Layout>
              </PermissionRoute>
            }
          />


          {/* Raíz: dirección al Dashboard; el resto, Mi Inicio hasta fichar y después el calendario */}
          <Route path="/" element={<RaizErp />} />

          {/* 404 - Not Found */}
          <Route
            path="*"
            element={
              <div className="flex flex-col items-center justify-center h-screen">
                <h1 className="text-4xl font-bold text-gray-900 mb-2">404</h1>
                <p className="text-gray-600 mb-4">Página no encontrada</p>
                <a href="/dashboard" className="text-duetto-primary hover:underline">
                  Volver al Dashboard
                </a>
              </div>
            }
          />
        </Routes>
        </GuiaProvider>
        </VistaEmpleadoProvider>
      </BrowserRouter>
    </AuthProvider>
    </HelmetProvider>
  );
}

export default App;
